import { IconBolt } from "@tabler/icons-react";
import { Trans } from "@lingui/macro";

import { useLocalize } from "../../content/useLocalize";
import type { Skill, SkillId, Tool, ToolId } from "../../types";
import { Panel } from "../common/Panel";
import { cx } from "../common/cx";
import styles from "./LoadoutPicker.module.css";

interface LoadoutPickerProps {
  tools: readonly Tool[];
  skills: readonly Skill[];
  skillLevels: Readonly<Record<string, number>>;
  activeToolIds: readonly string[];
  activeSkillIds: readonly string[];
  readOnly?: boolean;
  onToggleTool: (id: ToolId) => void;
  onToggleSkill: (id: SkillId) => void;
}

interface LoadoutRow {
  key: string;
  name: string;
  effect: string;
  active: boolean;
  onToggle: () => void;
}

/**
 * Owned tools and learned skills that apply to the current task. Everything is
 * on by default (the store enables the loadout when a contract starts); the
 * checkboxes only let the player switch a helper off to try the task unaided.
 * After submit the same list is shown read-only as "what helped here".
 */
export function LoadoutPicker({
  tools,
  skills,
  skillLevels,
  activeToolIds,
  activeSkillIds,
  readOnly = false,
  onToggleTool,
  onToggleSkill,
}: LoadoutPickerProps): JSX.Element | null {
  const t = useLocalize();

  const rows: LoadoutRow[] = [
    ...tools.map((tool) => ({
      key: `tool:${tool.id}`,
      name: t(tool.name),
      effect: t(tool.effect),
      active: activeToolIds.includes(tool.id),
      onToggle: () => onToggleTool(tool.id),
    })),
    ...skills.map((skill) => {
      const level = skillLevels[skill.id] ?? 0;
      const effect = skill.effects[level - 1];
      return {
        key: `skill:${skill.id}`,
        name: `${t(skill.name)} · ${level}`,
        effect: effect ? t(effect) : "",
        active: activeSkillIds.includes(skill.id),
        onToggle: () => onToggleSkill(skill.id),
      };
    }),
  ];

  if (rows.length === 0) {
    return null;
  }

  const activeRows = rows.filter((row) => row.active);

  if (readOnly) {
    if (activeRows.length === 0) {
      return null;
    }
    return (
      <p className={styles.summary}>
        <IconBolt size={14} aria-hidden="true" />
        <Trans>Помогали в этой задаче:</Trans>{" "}
        {activeRows.map((row) => row.name).join(", ")}
      </p>
    );
  }

  return (
    <Panel
      title={<Trans>Активно в задаче</Trans>}
      icon={<IconBolt size={17} />}
      actions={
        <span className={cx(styles.count, "mono")}>
          {activeRows.length} / {rows.length}
        </span>
      }
    >
      <p className={styles.loadoutHint}>
        <Trans>
          Купленные инструменты и прокачанные навыки уже включены и помогают здесь. Сними
          галочку, чтобы пройти задачу без подсказки.
        </Trans>
      </p>
      <ul className={styles.loadoutList}>
        {rows.map((row) => (
          <li key={row.key} className={cx(styles.loadoutItem, row.active && styles.loadoutItemActive)}>
            <label className={styles.loadoutLabel}>
              <input
                type="checkbox"
                className={styles.loadoutCheckbox}
                checked={row.active}
                onChange={row.onToggle}
              />
              <span className={styles.loadoutText}>
                <span className={cx(styles.loadoutName, !row.active && styles.loadoutInactive)}>
                  {row.name}
                </span>
                {row.effect && (
                  <span className={cx(styles.loadoutEffect, !row.active && styles.loadoutInactive)}>
                    {row.effect}
                  </span>
                )}
              </span>
              <span className={cx(styles.state, row.active ? styles.stateOn : styles.stateOff)}>
                {row.active ? <Trans>включено</Trans> : <Trans>выключено</Trans>}
              </span>
            </label>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
